
import {
  defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { ShopsProductsCategoriesList } from '@/types/generated-types/graphql';
import InputText from 'primevue/inputtext';
import ShopsProductsCategoriesListList from '@/composables/shops-products-categories-list/ShopsProductsCategories';

const PAGE_SIZE = 50;

export default defineComponent({
  name: 'CategorySelection',

  components: {
    Dialog,
    Button,
    InputText,
  },

  props: {
    defaultLang: {
      type: String,
      required: true,
    },

    availableCategoriesArr: {
      type: Array as PropType<ShopsProductsCategoriesList[]>,
      required: true,
    },

    chosenCategoriesArr: {
      type: Array as PropType<ShopsProductsCategoriesList[]>,
      required: true,
    },

    categorySearchStr: {
      type: String,
      required: true,
    },
  },

  emits: ['close-action-modal'],

  setup(props, context) {
    const { t } = useI18n();
    const dialogVisible = ref(true);
    const category = ref(props.categorySearchStr);
    const categoriesAvailableList = ref<ShopsProductsCategoriesList[]>([...props.availableCategoriesArr]);
    const categoriesChosenList = ref<ShopsProductsCategoriesList[]>([...props.chosenCategoriesArr]);
    // Stocker les valeurs initiales pour annulation
    const initialChosenList = [...props.chosenCategoriesArr];
    const availableListRef = ref<HTMLElement | null>(null);

    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMoreResults = ref(true);
    const currentOffset = ref(0);
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchRequestId = 0;

    const handleCancel = () => {
      dialogVisible.value = false;
      // Retourner les valeurs initiales
      context.emit('close-action-modal', initialChosenList, props.availableCategoriesArr, props.categorySearchStr);
    };

    const handleValidate = () => {
      dialogVisible.value = false;
      context.emit('close-action-modal', categoriesChosenList.value, categoriesAvailableList.value, category.value);
    };

    const updateDialogVisibility = (visible: boolean) => {
      if (!visible) {
        // Fermeture via croix = annulation
        handleCancel();
      }
    };

    const filterOutChosen = (categories: ShopsProductsCategoriesList[]): ShopsProductsCategoriesList[] => categories.filter(
      (c) => !categoriesChosenList.value.some((chosen) => chosen.id_shop_product_category_list === c.id_shop_product_category_list),
    );

    const handleSearch = async () => {
      if (searchTimer) clearTimeout(searchTimer);

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      const searchTerm = category.value.trim();
      if (!searchTerm) {
        categoriesAvailableList.value = [];
        isLoading.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        if (currentRequestId !== searchRequestId) return;

        isLoading.value = true;
        currentOffset.value = 0;
        hasMoreResults.value = true;
        categoriesAvailableList.value = [];

        const result = await ShopsProductsCategoriesListList(category.value, props.defaultLang, PAGE_SIZE, 0);

        if (result === null || currentRequestId !== searchRequestId) return;

        categoriesAvailableList.value = filterOutChosen(result);
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value = PAGE_SIZE;
        isLoading.value = false;
      }, 500);
    };

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreResults.value) return;

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      isLoadingMore.value = true;
      const result = await ShopsProductsCategoriesListList(category.value, props.defaultLang, PAGE_SIZE, currentOffset.value);

      if (result === null || currentRequestId !== searchRequestId) {
        isLoadingMore.value = false;
        return;
      }

      if (result.length > 0) {
        const newCategories = filterOutChosen(result);
        categoriesAvailableList.value = [...categoriesAvailableList.value, ...newCategories];
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value += PAGE_SIZE;
      } else {
        hasMoreResults.value = false;
      }
      isLoadingMore.value = false;
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    const selectItem = (categoryItem: ShopsProductsCategoriesList) => {
      categoriesChosenList.value.unshift(categoryItem);
      categoriesAvailableList.value = categoriesAvailableList.value.filter(
        (c) => c.id_shop_product_category_list !== categoryItem.id_shop_product_category_list,
      );
    };

    const removeItem = (categoryItem: ShopsProductsCategoriesList) => {
      categoriesChosenList.value = categoriesChosenList.value.filter(
        (c) => c.id_shop_product_category_list !== categoryItem.id_shop_product_category_list,
      );
    };

    return {
      t,
      dialogVisible,
      updateDialogVisibility,
      handleCancel,
      handleValidate,
      category,
      handleSearch,
      categoriesAvailableList,
      categoriesChosenList,
      availableListRef,
      selectItem,
      removeItem,
      handleAvailableScroll,
      isLoading,
      isLoadingMore,
      hasMoreResults,
    };
  },
});
